// tslint:disable:no-null-keyword
import { ErrorHandler, Injectable, InjectionToken } from '@angular/core';
import { Headers, Http, Response } from '@angular/http';
import { UUID } from 'angular2-uuid';
import * as moment from 'moment';
import { ENVIRONMENTS } from '../../environments/environments';
import { UniversalFilesystemService } from '../../vas/services/universal-filesystem/universal-filesystem.service';
import { StackTraceParser } from '../lib/stack-trace-parser';
import { ENV } from '../../environments/environment';

export interface SentryDsn {
    projectId: string;
    publicKey: string;
    secretKey: string;
    uri: string;
}

export const SENTRY_DSN_TOKEN =  new InjectionToken<UniversalFilesystemService>('sentry-dsn-token');

// tslint:disable-next-line:only-arrow-functions
export function errorHandlerServiceFactory(dsn: SentryDsn, http: Http): ErrorHandlerService {
    return new ErrorHandlerService(dsn, http);
}

/**
 * The ErrorHandlerService is our simple SDK for sending error events to Sentry.io.
 *
 * Sentry.io does actually offer their own SDK, however it's not compatible with the version of rxjs we have in the
 * project at the moment.
 *
 * The code in this class was inspired by the following documentation
 *  - https://develop.sentry.dev/sdk/store/
 *  - https://github.com/getsentry/sentry-javascript/blob/5.26.0/packages/core/src/api.ts
 */
@Injectable()
export class ErrorHandlerService implements ErrorHandler {

    constructor(
        protected dsn: SentryDsn,
        protected http: Http
    ) {

    }

    get projectId(): string {
        return this.dsn.projectId;
    }

    get publicKey(): string {
        return this.dsn.publicKey;
    }

    get secretKey(): string {
        return this.dsn.secretKey;
    }

    get storeUrl(): string {
        return `${this.uri}/api/${this.projectId}/store/`;
    }

    get uri(): string {
        return this.dsn.uri;
    }

    get headers(): object {
        return {
            'Content-Type': 'application/json',
            'X-Sentry-Auth':
                [
                    'Sentry sentry_version=7,',
                    `sentry_timestamp=${Date.now()},`,
                    `sentry_key=${this.publicKey},`,
                    `sentry_secret=${this.secretKey},`,
                    'sentry_client=vas-sentry/1.0'
                ].join('')
        };
    }

    getPayload(error: Error | Response): object {

        if (error instanceof Response) {
            return this.responseToPayload(error);
        }

        if (error instanceof Error) {
            return this.errorToPayload(error);
        }

        throw Error('error is not an instance of Error or Response');
    }

    responseToPayload(response: Response): object {
        return {
            event_id: UUID.UUID(),
            transaction: window.location.href,
            server_name: window.location.host,
            platform: 'javascript',
            timestamp: moment().toISOString(),
            exception: {
                values: [
                    {
                        type: response.statusText,
                        value: response.text(),
                        module: null
                    }
                ]
            }
        };
    }

    errorToPayload(error: Error): object {
        return {
            event_id: UUID.UUID(),
            transaction: window.location.href,
            server_name: window.location.host,
            platform: 'javascript',
            timestamp: moment().toISOString(),
            message: error.message,
            exception: {
                values: [
                    {
                        type: 'Exception',
                        value: error.message,
                        module: null,
                        stacktrace: {
                            frames: error.stack.split('\n')
                                .reverse()
                                .map(line => StackTraceParser.parse(line))
                                .filter(parsed => !!parsed)
                                .map(parsed => {
                                    return {
                                        filename: parsed.file,
                                        abs_path: parsed.file,
                                        function: parsed.methodName,
                                        in_app: true,
                                        colno: parsed.column,
                                        lineno: parsed.lineNumber,
                                        module: null
                                    };
                                })
                        }
                    }
                ]
            }
        };
    }

    handleError(error: Error): void {

        let payload;

        console.error(error);

        if (ENV.name === ENVIRONMENTS.DEV) {
            return;
        }

        try {
            payload = this.getPayload(error);
        } catch (e) {
            console.error('Failed to generate Sentry payload', e);
            return;
        }

        this.http.post(this.storeUrl, payload, {headers: new Headers(this.headers)})
            .subscribe(
                () => {
                    // noop
                },
                errorResponse => console.error(errorResponse)
            );
    }
}



// WEBPACK FOOTER //
// ./src/app/services/error-handler.service.ts